import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeftIcon } from '@heroicons/react/solid'
import UpdateSponsor from '../events/UpdateSponsor'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import IsAuth from '../../utillityComponents/IsAuth'
import PaymentConfirmation from './PaymentConfirmation'

export default function SponsorDetail() {
    const { event_slug, sponsor_id } = useParams()
    const [sponsor, setSponsor] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [showEdit, setShowEdit] = useState(false)
    const [participantsList, setParticipantsList] = useState([])
    const [bookedInvoices, setBookedInvoices] = useState([])
    const [draftInvoices, setDraftInvoices] = useState([])
    const [invoicesLoading, setInvoicesLoading] = useState(false)
    const [pdfLoading, setPdfLoading] = useState(null)
    const axiosPrivat = useAxiosPrivate()

    const canSeeInvoices = IsAuth(['Admin', 'Ngo'])

    useEffect(() => {
        let isMounted = true

        axiosPrivat.get(`/sponsor/get/${sponsor_id}`)
            .then((res) => {
                if (isMounted) {
                    setSponsor(res.data)
                    setLoading(false)
                }
            })
            .catch((err) => {
                console.error(err)
                if (isMounted) {
                    setError('Kunne ikke hente sponsor.')
                    setLoading(false)
                }
            })

        axiosPrivat.get(`/event/get/${event_slug}/participant/sponsor`)
            .then((res) => {
                if (isMounted) {
                    const list = (res.data || [])
                        .filter((p) => p.id !== '00000000-0000-0000-0000-000000000000')
                        .map((p) => ({ name: p.name, startnr: p.startnr }))
                    setParticipantsList(list)
                }
            })
            .catch((err) => console.error(err))

        if (canSeeInvoices) {
            setInvoicesLoading(true)
            Promise.all([
                axiosPrivat.get(`/sponsor/${sponsor_id}/invoice/booked`).catch(() => null),
                axiosPrivat.get(`/sponsor/${sponsor_id}/invoice/drafts`).catch(() => null),
            ]).then(([booked, drafts]) => {
                if (isMounted) {
                    setBookedInvoices(booked?.data?.collection ?? [])
                    setDraftInvoices(drafts?.data?.collection ?? [])
                    setInvoicesLoading(false)
                }
            })
        }

        return () => { isMounted = false }
    }, [sponsor_id, event_slug])

    const downloadInvoicePdf = async (invoiceNumber) => {
        if (pdfLoading) return
        setPdfLoading(invoiceNumber)
        try {
            const response = await axiosPrivat.get(
                `/sponsor/${sponsor_id}/invoice/booked/${invoiceNumber}/pdf`,
                { responseType: 'blob' }
            )
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `faktura-${invoiceNumber}.pdf`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } finally {
            setPdfLoading(null)
        }
    }

    if (loading) {
        return <p className="mt-8 text-center text-gray-500">Henter sponsor…</p>
    }

    if (error) {
        return (
            <div className="mt-8">
                <Link to={`/sponsors/${event_slug}`} className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800 mb-4">
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Tilbage
                </Link>
                <p className="text-red-600">{error}</p>
            </div>
        )
    }

    const Row = ({ label, value }) => (
        <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 border-b border-gray-100 last:border-0">
            <dt className="text-sm font-medium text-gray-500">{label}</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{value ?? '—'}</dd>
        </div>
    )

    return (
        <div className="mt-6">
            <Link to={`/sponsors/${event_slug}`} className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800 mb-6">
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Tilbage til sponsorliste
            </Link>

            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {sponsor.first_name} {sponsor.last_name}
                    </h2>
                    <button
                        type="button"
                        onClick={() => setShowEdit(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Rediger
                    </button>
                </div>

                <dl className="px-6 py-4">
                    <Row label="Email" value={sponsor.email} />
                    <Row label="Mobilnummer" value={sponsor.phone_number} />
                    <Row label="Kr/km" value={sponsor.donation_km} />
                    <Row label="Fast beløb" value={sponsor.donation_fixed} />
                    <Row label="Total donation" value={sponsor.total_donation} />
                    <Row label="Betalingstype" value={sponsor.payment_type} />
                    <Row
                        label="Betalt"
                        value={<PaymentConfirmation sponsor={sponsor} onChange={setSponsor} showUndo />}
                    />
                    <Row
                        label="Betalingsnotifikation sendt"
                        value={(() => {
                            switch (sponsor.payment_type) {
                                case 'invoice':
                                    return sponsor.received_invoice_at
                                        ? new Date(sponsor.received_invoice_at).toLocaleString('da-DK')
                                        : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Nej</span>
                                case 'mobilepay':
                                    return sponsor.has_received_mobilepay
                                        ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Ja</span>
                                        : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Nej</span>
                                case 'credit_card':
                                    return sponsor.has_received_stripe
                                        ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Ja</span>
                                        : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Nej</span>
                                case 'transfer':
                                    return sponsor.has_received_mail
                                        ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Ja</span>
                                        : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Nej</span>
                                default:
                                    return '—'
                            }
                        })()}
                    />
                    <Row label="Oprettet" value={sponsor.created_at ? new Date(sponsor.created_at).toLocaleString('da-DK') : null} />
                </dl>
            </div>

            {canSeeInvoices && (bookedInvoices.length > 0 || draftInvoices.length > 0 || invoicesLoading) && (
                <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Fakturaer</h2>
                    </div>
                    {invoicesLoading ? (
                        <p className="px-6 py-4 text-sm text-gray-500">Henter fakturaer…</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left font-medium text-gray-500">Fakturanr.</th>
                                        <th className="px-4 py-2 text-left font-medium text-gray-500">Dato</th>
                                        <th className="px-4 py-2 text-right font-medium text-gray-500">Beløb</th>
                                        <th className="px-4 py-2 text-left font-medium text-gray-500">Status</th>
                                        <th className="px-4 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {bookedInvoices.map((inv) => (
                                        <tr key={inv.bookedInvoiceNumber}>
                                            <td className="px-4 py-2 text-gray-900">#{inv.bookedInvoiceNumber}</td>
                                            <td className="px-4 py-2 text-gray-700">{inv.date ? new Date(inv.date).toLocaleDateString('da-DK') : '—'}</td>
                                            <td className="px-4 py-2 text-right text-gray-700">{inv.grossAmount ? `${inv.grossAmount.toLocaleString('da-DK')} ${inv.currency}` : '—'}</td>
                                            <td className="px-4 py-2">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Bogført</span>
                                            </td>
                                            <td className="px-4 py-2 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => downloadInvoicePdf(inv.bookedInvoiceNumber)}
                                                    disabled={pdfLoading === inv.bookedInvoiceNumber}
                                                    className="text-sm text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                                                >
                                                    {pdfLoading === inv.bookedInvoiceNumber ? 'Henter…' : 'Download PDF'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {draftInvoices.map((inv) => (
                                        <tr key={inv.draftInvoiceNumber} className="text-gray-400">
                                            <td className="px-4 py-2">#{inv.draftInvoiceNumber}</td>
                                            <td className="px-4 py-2">{inv.date ? new Date(inv.date).toLocaleDateString('da-DK') : '—'}</td>
                                            <td className="px-4 py-2 text-right">{inv.grossAmount ? `${inv.grossAmount.toLocaleString('da-DK')} ${inv.currency}` : '—'}</td>
                                            <td className="px-4 py-2">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Kladde</span>
                                            </td>
                                            <td className="px-4 py-2"></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <UpdateSponsor
                participantsList={participantsList}
                show={showEdit}
                sponsorId={sponsor_id}
            />
        </div>
    )
}
